import ConfigService from '@/services/ConfigService.js'
import SentryService from '@/services/SentryService.js'
import Crashlytics from '@/services/Crashlytics.js'
import Facebook from '@/services/Facebook.js'
import { initializeStores } from '@/services/StoresService.js'
import AppService from '@/services/AppService.js'
import bootPlugins from '@/plugins/plugins.js'
import bootComponents from '@/plugins/components.js'
import { ref } from 'vue'
import StorageService from '@/services/StorageService.js'
import LocalesService from '@/services/LocalesService.js'
import { Settings, User } from '@/store/index.js'
import { useProgressManager } from '@/services/ProgressManager.js'
import { initializeAdMob } from '@/services/AdMob'
import Analytics from '@/services/Analytics.js'
import AuthUserStateService from '@/services/AuthUserStateService.js'
import SubService from '@/services/SubService.js'
import AuthService from '@/services/AuthService.js'
// import { requestTrackingPermission } from '@/services/AppTrackingService.js'

/**
 * Bootstraps the app by initializing services and plugins
 * @param app
 * @param router
 * @returns {Promise<void>}
 */
export async function bootApp ({ app, router }) {
  // Initialize remote error tracking as early as possible
  await SentryService.init(app, router)
    .catch(e => Crashlytics.recordExceptionWithStacktrace('Sentry initialization failed', e))

  await Promise.all([
    // Fetch and initialize remote config
    ConfigService.initializeConfig({ connectTimeout: 5000 }),
    // Initialize local storage
    initializeStores()
      .catch(e => Crashlytics.recordExceptionWithStacktrace('Store initialization failed', e)),
    // Set up app instance id
    AppService.init()
      .catch(e => Crashlytics.recordExceptionWithStacktrace('AppService initialization failed', e)),
    // Initialize Facebook SDK for web
    Facebook.init()
      .catch(e => Crashlytics.recordExceptionWithStacktrace('FB initialization failed', e)),
  ])

  // Initialize vue plugins
  bootPlugins({ app, router })

  // Initialize vue components
  bootComponents({ app })
}

/**
 * Startup function for the app. Provides progress feedback for the user. Invoked when the app is mounted.
 * @returns { startup: function, completed: ref<boolean> }
 */
export function useAppStartUp () {
  const isStartingUp = ref(false)
  const completed = ref(false)

  const {
    setProgressStage,
    setStageProgress,
    completeProgress,
    resetProgressStages
  } = useProgressManager()

  resetProgressStages()
  setProgressStage('LOCALISATION', 0, 20)
  setProgressStage('SERVICES', 20, 100)

  const startup = async () => {
    if (isStartingUp.value) return
    isStartingUp.value = true

    try {
      // Load translations. Stop if it fails.
      await LocalesService.init(Settings.getLanguage() || 'en')

      setStageProgress('LOCALISATION', 100)

      // Initialize non-critical services
      // Initial sync of app instance info to get country code etc.,
      await AppService.syncAppInstanceInfo().catch((e) => {
        Crashlytics.recordExceptionWithStacktrace('AppService instance info sync failed', e)
      })

      // Set user ID for analytics and crashlytics
      const appInstanceId = AppService.getAppId()
      await Promise.allSettled([
        Analytics.setUserId(appInstanceId),
        Crashlytics.setUserId(appInstanceId)
      ])

      // Request tracking permission. Can trigger a consent dialog.
      // await requestTrackingPermission()
      //  .catch((err) => console.error('Error requesting tracking permission', err))

      await initializeAdMob().catch(e => {
        console.warn('Failed to initialize AdMob', e)
        Crashlytics.recordExceptionWithStacktrace('AdMob initialization failed', e)
      })

      // Check FB token and login state
      await Facebook.verify()
        .catch(e => {
          console.warn('Failed to verify Facebook token', e.message)
          // Crashlytics.recordExceptionWithStacktrace('Failed initializing authentication state', e)
        })

      // Sync user progress if needed
      await AuthUserStateService.init()
        .catch(e => Crashlytics.recordExceptionWithStacktrace('Failed to sync authenticated user progress', e))

      await AuthService.refresh3iToken(AuthService.getAuthUser())

      try {
        // Initialize RevenueCat
        const { id, email, nickname: displayName } = User.getUser() || {}
        await SubService.init({ id, email, displayName })
        // Refresh if possible
        SubService.refreshCustomerInfo()
      } catch (e) {
        Crashlytics.recordExceptionWithStacktrace('RevenueCat initialization failed', e)
      }

      setStageProgress('SERVICES', 100)

      completeProgress()

      completed.value = true
    } finally {
      isStartingUp.value = false
    }
  }

  return {
    startup,
    completed
  }
}

/**
 * Counts the app sessions and stores the count in local storage.
 * @returns {Promise<void>}
 */
export async function countAppSessions () {
  const now = new Date().getTime()

  let [
    sessionCount,
    sessionStartAt,
    sessionTimeout
  ] = await Promise.all([
    StorageService.get('session_count', 0),
    StorageService.get('session_start_at', 0),// MAYBE: improve by using session end time
    ConfigService.getNumber('session_timeout'),// 20 minutes in ms 20 * 60 * 1000
    ConfigService.getNumber('feedback_on_launch')
  ])

  // check if timeout is exceeded
  if (sessionStartAt + sessionTimeout < now) {
    // register a new session
    sessionCount++
    await Promise.all([
      StorageService.set('session_count', sessionCount),
      StorageService.set('session_start_at', now)
    ])
  }
}
